import { useState } from "react";
import { X, Copy, Check, ShareNetwork, Heart } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ShareDialog({ open, onClose, match }) {
  const [note, setNote] = useState("");
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shares", { match_user_id: match.user_id, note });
      setShare(data);
    } catch { toast.error("Could not create invite"); }
    setBusy(false);
  };

  const url = share ? `${window.location.origin}/invite/${share.code}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="share-dialog">
      <div className="bg-cream rounded-2xl w-full max-w-lg shadow-hero overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-2"><span className="rule" />Send a wingwoman</div>
            <h3 className="font-display text-2xl">Introduce her to a friend.</h3>
            <p className="text-sm text-ink/70 mt-1">Share an anonymous intro card. Nothing personal, just the journey.</p>
          </div>
          <button onClick={onClose} data-testid="share-close" className="p-1.5 rounded-full hover:bg-muted"><X size={18} /></button>
        </div>

        {!share ? (
          <div className="p-6 space-y-4">
            <div className="bg-white border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest"><Heart size={12} weight="duotone" className="text-coral-700" />Preview</div>
              <div className="font-display text-xl mt-2">You'll be introduced to <span className="italic text-coral-700">{match.anon_name}</span></div>
              <div className="text-sm text-ink/70 mt-1">{match.score}% shared journey · {match.stage}</div>
              {match.shared?.conditions?.length > 0 && (
                <div className="text-xs text-sage-700 mt-2">Shared: {match.shared.conditions.slice(0,3).join(" · ")}</div>
              )}
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">A note for your friend (optional)</label>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={280}
                data-testid="share-note"
                placeholder="Hey, I found someone whose journey mirrors yours. Might be worth a look."
                className="mt-1 w-full bg-white border border-border rounded-xl p-3 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none text-sm" />
            </div>
            <button onClick={create} disabled={busy} data-testid="share-generate"
              className="w-full bg-coral text-white py-3 rounded-full font-medium hover:bg-coral-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
              <ShareNetwork size={16} weight="duotone" /> {busy ? "Creating…" : "Generate invite link"}
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="bg-white border border-border rounded-xl p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Your invite is ready</div>
              <div className="mt-3 flex items-center gap-2 bg-muted rounded-xl p-3">
                <div className="flex-1 truncate text-sm font-mono">{url}</div>
                <button onClick={copy} data-testid="share-copy"
                  className="flex items-center gap-1.5 bg-ink text-cream px-3 py-1.5 rounded-full text-xs hover:bg-coral transition-colors">
                  {copied ? <><Check size={12} weight="bold" />Copied</> : <><Copy size={12} />Copy</>}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                Anyone with this link sees the intro card (no personal info) and can join Wingwomen.
                It stays anonymous, {match.anon_name} won't know unless they respond.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
